'use client';
import Link from 'next/link';
import { COLORS } from '@/common/constants';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import { startOfDay, endOfDay } from 'date-fns';
import { vi } from 'date-fns/locale';
import { useLocale, useTranslations } from 'use-intl';
import { fetchPostss } from '@/common/pageConfig';
import { twMerge } from 'tailwind-merge';
import DatePickerModal from '../../../components/DatePickerModal';
import { checkType, formatDateVN } from '@/common/ultils';

const ITEMS_PER_PAGE = 9;

async function getBlogPosts(text, languageCode, page, itemPerPage, tag, date, websiteCode) {
  try {
    const res = await fetchPostss(websiteCode, {
      isPublic: true,
      populate: 'sourceId',
      limit: itemPerPage,
      titleContent: text || undefined,
      languageCode,
      sortBy: 'createdAt:desc',
      page,
      tagNames: [tag],
      startIssuanceDate: date
        ? startOfDay(date, {
            locale: vi,
          }).toISOString()
        : undefined,
      endIssuanceDate: date
        ? endOfDay(date, {
            locale: vi,
          }).toISOString()
        : undefined,
    });
    return res;
  } catch {
    return { results: [], pagination: { total: 0 } };
  }
}

export default function PostsSearch({ text, date, tag, websiteCode, deviceType }) {
  const t = useTranslations('');

  const { slug } = useParams();
  const locale = useLocale();

  const [mainPost, setMainPost] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dateState, setDateState] = useState(date);
  const [dataDateInput, setDataDateInput] = useState(date);

  useEffect(() => {
    if (locale) {
      const fetchData = async () => {
        const data = await getBlogPosts(text, locale, currentPage, ITEMS_PER_PAGE, tag, dateState, websiteCode);

        setMainPost(data?.results ?? []);
        setTotalPages(Math.ceil(data?.totalResults / ITEMS_PER_PAGE));
      };
      fetchData();
    }
  }, [locale, currentPage, text, dateState]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <>
      <h1 className={twMerge('text-2xl font-bold mb-3 uppercase mt-3', COLORS.primary)}>
        {t('articleDetail.searchResult')} : {text || tag || dateState}
      </h1>
      <div className="flex mb-2">
        <DatePickerModal value={dataDateInput} onChange={(d) => setDataDateInput(d)} />
        <button onClick={() => setDateState(dataDateInput)} className="bg-blue-600 text-white px-4 ml-2 py-1 rounded-lg hover:bg-blue-700">
          {t('home.find')}
        </button>
      </div>
      {!isEmpty(mainPost) ? (
        <div className="flex flex-wrap gap-4">
          {mainPost.map((post) => (
            <article key={post.id} className="md:w-[32%] w-full mb-6 border border-gray-300 rounded-sm overflow-hidden bg-white flex flex-col">
              <div className="relative h-[250px] p-5 border-b flex">
                {post.imgaeUrls ? (
                  <img src={checkType(deviceType, post)} alt={post.title || 'Ảnh thumbnail'} className="block m-auto w-full h-full object-cover" />
                ) : (
                  <img src={`/img-default.png`} alt="ảnh mặc định" className="w-12.5 h-12.5 mx-auto my-auto rounded" />
                )}
              </div>

              <div className="p-4 flex flex-col justify-between flex-grow">
                <h3 className={twMerge('text-md font-semibold mb-2 transition-colors line-clamp-2', COLORS.greenLightHover)}>
                  <Link href={websiteCode ? `/sub/${websiteCode}/${slug}/${post?.slug}` : `/${slug}/${post?.slug}`} className="hover:underline">
                    {post.title}
                  </Link>
                </h3>

                <div className="text-gray-500 text-xs mb-3 flex justify-between italic">
                  <div className="mr-7">
                    <ClockCircleOutlined className="mr-1" />
                    {!!(post?.postedDate ?? post?.date) ? formatDateVN(post?.postedDate ?? post?.date) : ''}
                  </div>

                  <div>
                    {locale == 'vi' ? 'Nguồn' : 'Source'}: {post?.source?.name ?? post?.createdBy?.name ?? 'admin'}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="text-md font-normal text-black mb-3 ">{t('news.noData')}</p>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex flex-wrap justify-center space-x-2 w-full">
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((page) => {
              return (
                page <= 3 || // 3 đầu
                page > totalPages - 3 || // 3 cuối
                (page >= currentPage - 1 && page <= currentPage + 1) // xung quanh trang hiện tại
              );
            })
            .reduce((acc, page, i, arr) => {
              if (i > 0 && page - arr[i - 1] > 1) {
                acc.push('ellipsis-' + i); // Dùng key khác biệt
              }
              acc.push(page);
              return acc;
            }, [])
            .map((item) => {
              if (typeof item === 'string' && item.startsWith('ellipsis')) {
                const middle = Math.floor(totalPages / 2);
                return (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(middle)}
                    className="px-3 py-1 border rounded bg-white text-gray-500 border-gray-300">
                    ...
                  </button>
                );
              } else {
                return (
                  <button
                    key={item}
                    onClick={() => handlePageChange(item)}
                    className={`px-3 py-1 border rounded ${
                      currentPage === item ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border-gray-300'
                    }`}>
                    {item}
                  </button>
                );
              }
            })}
        </div>
      )}
    </>
  );
}
